import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaCar, FaChargingStation, FaCogs, FaNetworkWired, FaMicrochip, FaGlobe,
    FaCheckCircle, FaArrowRight, FaIndustry
} from 'react-icons/fa';
import heroImage from '../../assets/Vibrant Urban Scenes and Artistic Designs for Your Projects.png';

const AutomotivePage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaCar, title: 'Software Defined Vehicles', desc: 'Operating systems and middleware for next-gen SDVs.', image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&q=80' },
        { icon: FaChargingStation, title: 'EV Ecosystem', desc: 'Charging infrastructure management and battery analytics.', image: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80' },
        { icon: FaNetworkWired, title: 'Connected Mobility', desc: 'V2X (Vehicle-to-Everything) communication platforms.', image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80' },
        { icon: FaCogs, title: 'Smart Supply Chain', desc: 'AI-driven logistics for just-in-time manufacturing.', image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80' },
        { icon: FaMicrochip, title: 'Autonomous Driving', desc: 'Data annotation and simulation for ADAS Level 3+.', image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80' },
        { icon: FaGlobe, title: 'Digital Dealership', desc: 'Immersive AR/VR showrooms and seamless buying journeys.', image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&q=80' }
    ];

    const faqs = [
        { q: 'How do you handle AUTOSAR compliance?', a: 'We have certified AUTOSAR engineers who build compliant stacks for ECUs and domain controllers.' },
        { q: 'Can you support OTA updates?', a: 'Yes, we build secure FOTA (Firmware Over-The-Air) pipelines to keep vehicle software up to date remotely.' },
        { q: 'What is your experience with EVs?', a: 'We work with top OEMs on BMS (Battery Management Systems) and charging station apps.' },
        { q: 'Do you offer ADAS simulation?', a: 'Yes, we provide high-fidelity simulation environments to test ADAS algorithms safely before road trials.' },
        { q: 'How secure are your V2X solutions?', a: 'We implement PKI infrastructures and hardware security modules (HSM) to ensure tamper-proof V2X communication.' },
        { q: 'Can you help with digital cockpits?', a: 'Absolutely. We specialize in Android Automotive OS (AAOS) and QNX-based infotainment systems integration.' }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-top"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-slate-300 mb-4">Industries &gt; Manufacturing</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Automotive <span className="font-bold">Mobility</span></h1>
                        <p className="text-lg font-light text-slate-200 leading-relaxed mb-8">
                            Accelerating the shift to electric, connected, and autonomous vehicles.
                        </p>
                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-3 bg-blue-600 text-white font-medium rounded-sm hover:bg-blue-700 transition-all text-sm uppercase tracking-wide">
                                Consult Experts
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Introduction - HCL Style Text Left, Image Right */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-light mb-6">Redefining Mobility</h2>
                            <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                The automotive industry is no longer just about moving parts; it's about moving data. Tech-Inovant helps OEMs and Tier-1 suppliers transition seamlessly from hardware-centric to software-defined business models.
                            </p>
                            <p className="text-slate-600 leading-relaxed mb-8">
                                From the factory floor to the driver's seat, we engineer digital solutions that enhance safety, efficiency, and customer experience.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-blue-600" />
                                    <span className="text-slate-700 font-medium">Accelerated R&D Cycles</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-blue-600" />
                                    <span className="text-slate-700 font-medium">Seamless Digital Cockpits</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-blue-600" />
                                    <span className="text-slate-700 font-medium">Sustainable Manufacturing</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1617788138017-80ad40651399?w=800&q=80"
                                alt="Automotive Innovation"
                                className="rounded-sm shadow-sm grayscale hover:grayscale-0 transition-all duration-700"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6 rounded-sm shadow-lg hidden md:block">
                                <div className="text-3xl font-bold">15+</div>
                                <div className="text-xs uppercase tracking-wider">OEM Partners</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Grid - Clean Cards, No Heavy Shadows */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="mb-16">
                        <h2 className="text-3xl font-light mb-4">Moblity Solutions</h2>
                        <div className="w-20 h-1 bg-blue-600"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <div key={idx} className="bg-white group border border-slate-200 hover:border-blue-400 transition-colors p-0 overflow-hidden">
                                <div className="h-48 overflow-hidden relative">
                                    {/* Image with NO overlay/shadow as requested */}
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-8">
                                    <service.icon className="text-3xl text-blue-600 mb-4" />
                                    <h3 className="text-xl font-medium mb-3 text-slate-900">{service.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed mb-6">
                                        {service.desc}
                                    </p>
                                    <a href="#" className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-800 tracking-wide uppercase">
                                        Learn More <FaArrowRight className="ml-2 text-xs" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Case Study / Impact Section */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <h2 className="text-3xl font-light mb-8 text-center">Engineering Impact</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { title: 'Global EV Maker', stat: '40% Faster', desc: 'Reduced battery simulation time.' },
                            { title: 'Tier-1 Supplier', stat: 'Zero', desc: 'Defects in safety-critical software.' },
                            { title: 'Luxury Brand', stat: '2.5M', desc: 'Connected cars managed on our platform.' }
                        ].map((caseStudy, idx) => (
                            <div key={idx} className="bg-slate-50 p-8 border-l-4 border-blue-600">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{caseStudy.title}</div>
                                <div className="text-4xl font-light text-blue-900 mb-4">{caseStudy.stat}</div>
                                <p className="text-slate-600">{caseStudy.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Section */}
            <section id="contact" className="py-12 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-light mb-8 text-slate-900">Common Questions</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-white border border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center"
                                        >
                                            <span className="font-medium text-slate-800">{faq.q}</span>
                                            <span className="text-slate-400">{openFaq === idx ? '-' : '+'}</span>
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                                                {faq.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-200">
                            <h3 className="text-2xl font-light mb-6 text-slate-900">Get Expert Advice</h3>
                            <p className="text-slate-600 mb-8">Speak with our automotive engineers today.</p>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="John Doe" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                    <input type="text" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="Auto Corp" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors" placeholder="john@autocorp.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Interest</label>
                                    <select className="w-full bg-slate-50 px-4 py-3 border border-slate-200 focus:border-blue-500 outline-none transition-colors text-slate-700">
                                        <option>SDV & OS</option>
                                        <option>EV Systems</option>
                                        <option>Autonomous Driving</option>
                                        <option>Supply Chain</option>
                                    </select>
                                </div>
                                <button type="submit" className="w-full bg-blue-600 text-white font-bold py-4 rounded-sm hover:bg-blue-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Request Consultation
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AutomotivePage;
